// src/dataTypes/liveStreamManagment/utils_videoSource.ts
//
// Source d'un <video> du mediaboard : soit un fichier local (blob:/data:, rangé
// dans IndexedDB), soit une vidéo du compte lue en HLS sur le serveur média
// public (bouton « Library »).
//
// Chrome ne lit pas le HLS dans `video.src` : hls.js s'en charge, et son instance
// doit être détruite avec l'élément — sinon elle continue de télécharger des
// segments pour une vidéo que plus personne ne regarde. D'où le couple
// setVideoSource / releaseVideoSource, à utiliser à la place de `video.src = …`
// partout où une url de mediaboard est lue.

import Hls from 'hls.js';

const players = new WeakMap<HTMLVideoElement, Hls>();

/**
 * Média lu à distance (vidéo HLS du compte) plutôt qu'un fichier rangé dans
 * IndexedDB : il n'a ni `instantMediaId` ni fichier à téléverser, et son url
 * se garde telle quelle dans les sauvegardes.
 */
export const isStreamedMediaUrl = (url: string | undefined): boolean =>
  !!url && /^https?:\/\//i.test(url);

export const isHlsUrl = (url: string | undefined): boolean =>
  !!url && /\.m3u8(\?|#|$)/i.test(url);

/** Branche `url` sur `el` (hls.js pour un .m3u8, `src` sinon). Remplace toute source HLS précédente. */
export function setVideoSource(el: HTMLVideoElement, url: string): void {
  releaseVideoSource(el);

  if (!isHlsUrl(url)) { el.src = url; return; }

  // Indispensable pour dessiner la vidéo dans un canvas et pour reprendre son son
  // dans Web Audio : sans lui, l'image est « tainted » et le son muet. Le serveur
  // média répond `Access-Control-Allow-Origin: *`.
  el.crossOrigin = 'anonymous';

  if (!Hls.isSupported()) { el.src = url; return; }   // Safari : HLS natif

  const hls = new Hls({ capLevelToPlayerSize: false });

  // La régie diffuse en 1080p : on démarre sur le meilleur rendu plutôt que de
  // laisser l'ABR partir du plus bas (les éléments cachés font 1 px).
  hls.on(Hls.Events.MANIFEST_PARSED, () => { hls.startLevel = hls.levels.length - 1; });

  hls.on(Hls.Events.ERROR, (_e, data) => {
    if (!data.fatal) return;
    if (data.type === Hls.ErrorTypes.NETWORK_ERROR) { hls.startLoad(); return; }
    if (data.type === Hls.ErrorTypes.MEDIA_ERROR)   { hls.recoverMediaError(); return; }
    console.warn('[videoSource] HLS irrécupérable', url, data.details);
    releaseVideoSource(el);
    el.dispatchEvent(new Event('error'));
  });

  hls.loadSource(url);
  hls.attachMedia(el);
  players.set(el, hls);
}

/** Détache l'éventuel lecteur hls.js de `el`. Sans effet pour une source fichier. */
export function releaseVideoSource(el: HTMLVideoElement): void {
  const hls = players.get(el);
  if (!hls) return;
  players.delete(el);
  try { hls.destroy(); } catch { /* best-effort */ }
}

/**
 * Url HLS publique d'une vidéo du compte : `<publicRef>/videos/<rendu>/index.m3u8`,
 * le rendu étant `Video::videoFile` ('001').
 */
export function hlsUrlOfVideo(mediaServer: string, publicRef: string, videoFile: string | undefined): string {
  return `${mediaServer}${publicRef}/videos/${videoFile || '001'}/index.m3u8`;
}

/**
 * Vignette publique d'une vidéo HLS du compte (`<publicRef>/images/thumbnail.webp`),
 * pour les miniatures : un lecteur hls.js par case pour n'en montrer qu'une
 * image téléchargerait des segments pour rien. null si l'url n'a pas cette forme.
 */
export function posterOfHlsUrl(url: string): string | null {
  const m = url.match(/^(.*)\/videos\/[^/]+\/index\.m3u8(?:[?#].*)?$/i);
  return m ? `${m[1]}/images/thumbnail.webp` : null;
}
